import { isAngleBetweenCCW } from '../../../../../tool/isAngleBetweenCCW';
import { normalizeAngle } from '../../../../../tool/normalizeAngle';
import { normalizeArcAngles } from '../../../../../tool/normalizeArcAngles';
import type { ReferencePlane } from '../../../../../reference/ReferencePlane';
import type { EllipseArcProjectionResult } from '../../../model/ProjectionResult';
import type { ResolvedEllipseArcReferenceSource } from '../SketchReferenceSourceResolver';
import type { SupportProjectionPreprocessor } from './SupportProjectionPreprocessor';
import {
	buildProjectedRotatedEllipseBasis,
	decomposeProjectedEllipseBasis,
	isProjectedLine,
	localPointToWorldCoordinate,
	resolveEllipseParameterAngle,
	resolveProjectedLocalPointByParameter,
} from './SupportProjectionPreprocessor';

/**
 * 椭圆弧源投影预处理器。
 * 说明：
 * 1. 部分椭圆在 SolidWorks 中仍属于可转换的草图曲线源；
 * 2. 投影时保留弧段参数语义，目标端由 `ellipse-arc` builder 承接；
 * 3. 当短轴退化为线时，使用弧段扫掠范围计算有效线段区间，避免遗漏内部极值点。
 */
export class EllipseArcSupportProjectionPreprocessor
	implements SupportProjectionPreprocessor<ResolvedEllipseArcReferenceSource, EllipseArcProjectionResult>
{
	/**
	 * 执行椭圆弧投影。
	 *
	 * @param input 投影输入。
	 * @returns 成功时返回 ellipse-arc / line，完全退化时返回 null。
	 */
	public projectToTargetPlane(input: {
		source: ResolvedEllipseArcReferenceSource;
		targetReferencePlane: ReferencePlane;
	}): EllipseArcProjectionResult | null {
		const projectedBasis = buildProjectedRotatedEllipseBasis({
			centerWorld: input.source.centerWorld,
			planeXAxisWorld: input.source.planeXAxisWorld,
			planeYAxisWorld: input.source.planeYAxisWorld,
			radiusX: input.source.radiusX,
			radiusY: input.source.radiusY,
			rotation: input.source.rotation,
			targetReferencePlane: input.targetReferencePlane,
		});
		const ellipse = decomposeProjectedEllipseBasis(projectedBasis);
		if (!ellipse) {
			return null;
		}

		if (isProjectedLine(ellipse)) {
			return this.buildLineProjectionResult({
				source: input.source,
				ellipse,
				projectedBasis,
				targetReferencePlane: input.targetReferencePlane,
			});
		}

		const projectedStartLocal = resolveProjectedLocalPointByParameter(
			projectedBasis,
			input.source.startAngle,
		);
		const projectedEndLocal = resolveProjectedLocalPointByParameter(
			projectedBasis,
			input.source.endAngle,
		);
		const projectedMidLocal = resolveProjectedLocalPointByParameter(
			projectedBasis,
			this.resolveEllipseArcMidParameter(input.source),
		);
		const startParameter = resolveEllipseParameterAngle(projectedStartLocal, ellipse);
		const endParameter = resolveEllipseParameterAngle(projectedEndLocal, ellipse);
		const midParameter = resolveEllipseParameterAngle(projectedMidLocal, ellipse);
		if (startParameter === null || endParameter === null || midParameter === null) {
			return null;
		}

		const normalizedStart = normalizeAngle(startParameter);
		const normalizedEnd = normalizeAngle(endParameter);
		const normalizedMid = normalizeAngle(midParameter);
		const clockwise = !isAngleBetweenCCW(normalizedStart, normalizedEnd, normalizedMid);
		const normalizedAngles = normalizeArcAngles(normalizedStart, normalizedEnd, clockwise);

		return {
			sourceDescriptor: input.source.sourceDescriptor,
			sourceKind: 'ellipse-arc',
			projectionResultKind: 'ellipse-arc',
			diagnostics: [],
			geometryPayload: {
				centerWorld: localPointToWorldCoordinate(ellipse.centerLocal, input.targetReferencePlane),
				radiusX: ellipse.radiusX,
				radiusY: ellipse.radiusY,
				rotation: ellipse.rotation,
				startAngle: normalizedAngles.start,
				endAngle: normalizedAngles.end,
				clockwise,
			},
		};
	}

	/**
	 * 解析椭圆弧中点参数角。
	 *
	 * @param source 椭圆弧来源快照。
	 * @returns 弧段中点参数角。
	 */
	private resolveEllipseArcMidParameter(source: ResolvedEllipseArcReferenceSource): number {
		const normalized = normalizeArcAngles(
			source.startAngle,
			source.endAngle,
			source.clockwise,
		);
		return source.clockwise
			? normalized.start - normalized.angleRange * 0.5
			: normalized.start + normalized.angleRange * 0.5;
	}

	/**
	 * 构造 ellipse-arc -> line 的退化投影结果。
	 *
	 * @param input 线退化构造输入。
	 * @returns 线投影结果。
	 */
	private buildLineProjectionResult(input: {
		source: ResolvedEllipseArcReferenceSource;
		ellipse: import('./SupportProjectionPreprocessor').DecomposedProjectedEllipse;
		projectedBasis: import('./SupportProjectionPreprocessor').ProjectedPlanarBasis;
		targetReferencePlane: ReferencePlane;
	}): EllipseArcProjectionResult {
		const phase = Math.atan2(
			input.projectedBasis.axisYLocal.dot(input.ellipse.majorAxisUnitLocal),
			input.projectedBasis.axisXLocal.dot(input.ellipse.majorAxisUnitLocal),
		);
		const candidateParameters = [
			input.source.startAngle,
			input.source.endAngle,
			this.resolveEllipseArcMidParameter(input.source),
		];
		const normalizedSweep = normalizeArcAngles(
			normalizeAngle(input.source.startAngle),
			normalizeAngle(input.source.endAngle),
			input.source.clockwise,
		);
		[phase, phase + Math.PI].forEach((candidate) => {
			if (this.isParameterWithinArcSweep(
				candidate,
				normalizedSweep.start,
				normalizedSweep.end,
				input.source.clockwise,
			)) {
				candidateParameters.push(candidate);
			}
		});

		const scalars = candidateParameters.map((parameter) => {
			const projectedPoint = resolveProjectedLocalPointByParameter(input.projectedBasis, parameter);
			return projectedPoint.clone().sub(input.ellipse.centerLocal)
				.dot(input.ellipse.majorAxisUnitLocal);
		});
		const startScalar = Math.min(...scalars);
		const endScalar = Math.max(...scalars);
		const startLocal = input.ellipse.centerLocal.clone()
			.addScaledVector(input.ellipse.majorAxisUnitLocal, startScalar);
		const endLocal = input.ellipse.centerLocal.clone()
			.addScaledVector(input.ellipse.majorAxisUnitLocal, endScalar);

		return {
			sourceDescriptor: input.source.sourceDescriptor,
			sourceKind: 'ellipse-arc',
			projectionResultKind: 'line',
			diagnostics: [],
			geometryPayload: {
				startWorld: localPointToWorldCoordinate(startLocal, input.targetReferencePlane),
				endWorld: localPointToWorldCoordinate(endLocal, input.targetReferencePlane),
			},
		};
	}

	/**
	 * 判断参数角是否落在椭圆弧扫掠范围内。
	 *
	 * @param parameter 待判断参数角。
	 * @param normalizedStart 归一化后的起始角。
	 * @param normalizedEnd 归一化后的终止角。
	 * @param clockwise 是否顺时针。
	 * @returns 命中弧段扫掠范围时返回 true。
	 */
	private isParameterWithinArcSweep(
		parameter: number,
		normalizedStart: number,
		normalizedEnd: number,
		clockwise: boolean,
	): boolean {
		const normalizedParameter = normalizeAngle(parameter);
		if (!clockwise) {
			return isAngleBetweenCCW(normalizedStart, normalizedEnd, normalizedParameter);
		}

		return isAngleBetweenCCW(normalizedEnd, normalizedStart, normalizedParameter);
	}
}
